import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Normalize string or { value, label } options into a consistent shape.
 */
export function normalizeAimTagOptions(options = []) {
  return (options || [])
    .map((option) => {
      if (option == null) return null;
      if (typeof option === 'string' || typeof option === 'number') {
        const value = String(option);
        return { value, label: value };
      }
      const value = option.value ?? option.id;
      if (value == null || value === '') return null;
      const label = option.label != null && option.label !== ''
        ? String(option.label)
        : String(value);
      return { value: String(value), label };
    })
    .filter(Boolean);
}

/**
 * Multi-select tag chips for Lighthouse (aim / cohort) and admin filters.
 * Options may be plain strings or `{ value, label }` objects.
 */
export function AimTagFilter({
  options = [],
  value = [],
  onChange,
  addLabel = '+ Aim',
  emptyLabel = 'No more aims',
  ariaLabel = 'Primary aim filters',
  testId = 'onyx-aim-filter',
  itemNoun = 'aim',
  required = false,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);
  const normalizedOptions = normalizeAimTagOptions(options);
  const selected = (Array.isArray(value) ? value : [])
    .map((item) => String(item))
    .filter(Boolean);
  const selectedSet = new Set(selected);
  const labelByValue = new Map(normalizedOptions.map((opt) => [opt.value, opt.label]));
  const available = normalizedOptions.filter((opt) => !selectedSet.has(opt.value));

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const addValue = (nextValue) => {
    if (!nextValue || selectedSet.has(nextValue)) return;
    onChange([...selected, nextValue]);
    setMenuOpen(false);
  };

  const removeValue = (nextValue) => {
    onChange(selected.filter((item) => item !== nextValue));
  };

  return (
    <div
      className={`onyx-aim-filter${menuOpen ? ' is-open' : ''}`}
      ref={rootRef}
      data-testid={testId}
      aria-label={ariaLabel}
    >
      {selected.map((selectedValue) => (
        <span key={selectedValue} className="onyx-aim-filter-tag">
          <span className="onyx-aim-filter-tag-label">
            {labelByValue.get(selectedValue) || selectedValue}
          </span>
          <button
            type="button"
            className="onyx-aim-filter-tag-remove"
            aria-label={`Remove ${labelByValue.get(selectedValue) || selectedValue} ${itemNoun} filter`}
            onClick={() => removeValue(selectedValue)}
          >
            ×
          </button>
        </span>
      ))}
      <div className="onyx-aim-filter-add">
        <button
          type="button"
          className="onyx-aim-filter-add-btn"
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {addLabel}
          {required ? (
            <span className="onyx-note-required-dot" aria-hidden="true" />
          ) : null}
        </button>
        {menuOpen ? (
          <div className="onyx-aim-filter-menu">
            <ul className="onyx-aim-filter-options" role="listbox">
              {available.length === 0 ? (
                <li className="onyx-aim-filter-empty">{emptyLabel}</li>
              ) : (
                available.map((opt) => (
                  <li key={opt.value} role="option">
                    <button
                      type="button"
                      className="onyx-aim-filter-option"
                      onClick={() => addValue(opt.value)}
                    >
                      <span className="onyx-aim-tag">{opt.label}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

AimTagFilter.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
      }),
    ])
  ),
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func.isRequired,
  addLabel: PropTypes.string,
  emptyLabel: PropTypes.string,
  ariaLabel: PropTypes.string,
  testId: PropTypes.string,
  itemNoun: PropTypes.string,
  required: PropTypes.bool,
};
